import {Subject, filter} from 'rxjs';

export enum BusEventEnum {
    SNACKBAR_MESSAGE = 'SNACKBAR_MESSAGE',
    CHECKOUT_SUBMIT_PAYMENT = 'CHECKOUT_SUBMIT_PAYMENT',
    CHECKOUT_SUBMIT_REGISTER = 'CHECKOUT_SUBMIT_REGISTER',
    USER_INTERACTION = 'USER_INTERACTION',
    VIDEO_CHAPTER_SEEK = 'VIDEO_CHAPTER_SEEK',
    SUPER_COURSE_LESSON_COMPLETE = 'SUPER_COURSE_LESSON_COMPLETE',
    CLOSE_BUTTON_IS_PROTECTED = 'CLOSE_BUTTON_IS_PROTECTED',
    VIDEO_PLAYER_CLOSE_MENU = 'VIDEO_PLAYER_CLOSE_MENU',
}

const RESPONSE_KEY = ':res';

export interface IEvent<T = unknown> {
    key: string;
    data?: T;
    response?: {error?: Error; data?: T};
    timestamp: number;
}

const EventSubject = new Subject<IEvent>();

const getEventSubject = <T>() => EventSubject as Subject<IEvent<T>>;

const on = <T>(key: string, callback: (event: IEvent<T>) => void) => {
    const sub = getEventSubject<T>()
        .pipe(filter(event => event.key === key))
        .subscribe(callback);
    return sub;
};

const onAsync = <T, K>(
    key: string,
    callback: (event: IEvent<T>) => Promise<K>,
) => {
    const sub = on<T>(key, async responseEvent => {
        try {
            const data = await callback(responseEvent);
            respond({
                timestamp: responseEvent.timestamp,
                key: responseEvent.key,
                response: {
                    data,
                },
            });
        } catch (error) {
            respond({
                timestamp: responseEvent.timestamp,
                key: responseEvent.key,
                response: {
                    error: error as Error,
                },
            });
        }
    });
    return sub;
};

const send = <T>(event: Omit<IEvent<T>, 'response' | 'timestamp'>) => {
    getEventSubject<T>().next({...event, timestamp: new Date().getTime()});
};

const sendAsync = <T>(event: Omit<IEvent<T>, 'response' | 'timestamp'>) => {
    const ev = {...event, timestamp: new Date().getTime()};
    return new Promise((resolve, reject) => {
        const sub = on(event.key + RESPONSE_KEY, responseEvent => {
            if (ev.timestamp !== responseEvent.timestamp) {
                return;
            }
            if (responseEvent?.response?.error) {
                reject(responseEvent.response.error);
            } else {
                resolve(responseEvent?.response?.data);
            }
            sub.unsubscribe();
        });
        getEventSubject<T>().next(ev);
    });
};

const respond = <T>(event: Omit<IEvent<T>, 'data'>) => {
    getEventSubject<T>().next({...event, key: event.key + RESPONSE_KEY});
};

export const EventBus = {
    on,
    onAsync,
    send,
    sendAsync,
    respond,
};
